import { API_HEAD } from '@/lib/apiConfig';

export interface Permission {
  id: number;
  code: string;
  name: string;
  description?: string;
  category?: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface PermissionsResponse {
  success: boolean;
  data: Permission[];
}

export interface PermissionsByCategoryResponse {
  success: boolean;
  data: Record<string, Permission[]>;
}

export interface StaffPermissionsResponse {
  success: boolean;
  data: {
    staff: {
      id: number;
      fullName: string;
      email: string;
      role: string;
    };
    permissions: Permission[];
  };
}

export interface MyPermissionsResponse {
  success: boolean;
  data: {
    role: string;
    permissions: string[];
    isAdmin: boolean;
  };
}

class PermissionService {
  private getAuthToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('adminToken');
  }

  private getHeaders(): HeadersInit {
    const token = this.getAuthToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  async getAllPermissions(): Promise<PermissionsResponse> {
    const response = await fetch(`${API_HEAD}admin/permissions`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Lỗi khi tải danh sách chức năng');
    }

    return response.json();
  }

  async getPermissionsByCategory(): Promise<PermissionsByCategoryResponse> {
    const response = await fetch(`${API_HEAD}admin/permissions/by-category`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Lỗi khi tải danh sách chức năng');
    }

    return response.json();
  }

  async getStaffPermissions(staffId: string): Promise<StaffPermissionsResponse> {
    const response = await fetch(`${API_HEAD}admin/permissions/staff/${staffId}`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Lỗi khi tải chức năng của nhân viên');
    }

    return response.json();
  }

  async assignPermissionsToStaff(staffId: string, permissionIds: number[]): Promise<StaffPermissionsResponse> {
    const response = await fetch(`${API_HEAD}admin/permissions/staff/${staffId}/assign`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify({ permissionIds }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Lỗi khi cập nhật chức năng cho nhân viên');
    }

    return response.json();
  }

  async getMyPermissions(): Promise<MyPermissionsResponse> {
    const response = await fetch(`${API_HEAD}admin/permissions/my-permissions`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Lỗi khi tải quyền của bạn');
    }

    return response.json();
  }
}

export default new PermissionService();

